import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@components/Card';
import { Button } from '@components/Button';
import { Loading } from '@components/Loading';
import { useAuth } from '@hooks/useAuth';
import { projectService, type Project, type CreateProjectRequest } from '@services/projectService';
import { ProjectCard } from './ProjectCard';
import { NewProjectModal } from './NewProjectModal';

/**
 * Dashboard page - Main landing page with modernization workflow
 */
export function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [stats, setStats] = useState({ total: 0, analyzing: 0, completed: 0 });

  useEffect(() => {
    loadProjects();
  }, []);

  // Poll for analyzing projects
  useEffect(() => {
    const analyzingProjects = projects.filter(p => p.analysis_status === 'analyzing');

    if (analyzingProjects.length === 0) return;

    const pollInterval = setInterval(async () => {
      for (const project of analyzingProjects) {
        try {
          // Get job_id from project analysis_results
          const analysisResults = project.analysis_results as any;
          const jobId = analysisResults?.graph_job_id;

          if (!jobId) {
            console.warn(`No job_id found for project ${project.id}`);
            continue;
          }

          // Check job status from code-graph-service
          const jobStatus = await projectService.checkJobStatus(jobId);

          if (jobStatus.status === 'completed') {
            // Update project status in core-platform-service
            await projectService.updateAnalysisStatus(
              project.id,
              'completed',
              {
                total_nodes: jobStatus.progress?.nodes_created || 0,
                total_relationships: jobStatus.progress?.relationships_created || 0,
                languages: jobStatus.progress?.languages_detected || [],
                total_files: jobStatus.progress?.files_total || 0,
              }
            );
            // Reload projects to reflect the change
            await loadProjects();
          } else if (jobStatus.status === 'failed') {
            // Update project status to failed
            await projectService.updateAnalysisStatus(
              project.id,
              'failed',
              undefined,
              jobStatus.error_message
            );
            // Reload projects
            await loadProjects();
          }
        } catch (err) {
          console.error(`Error polling job for project ${project.id}:`, err);
        }
      }
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(pollInterval);
  }, [projects]);

  const loadProjects = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await projectService.getProjects();
      setProjects(response.projects);

      // Calculate stats
      const analyzing = response.projects.filter(p => p.analysis_status === 'analyzing').length;
      const completed = response.projects.filter(p => ['analyzed', 'completed'].includes(p.analysis_status)).length;
      setStats({ total: response.total, analyzing, completed });
    } catch (err: any) {
      console.error('Failed to load projects:', err);
      setError(err.response?.data?.detail || 'Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateProject = async (data: CreateProjectRequest) => {
    try {
      const project = await projectService.createProject(data);
      // Automatically start analysis after project creation
      await projectService.startAnalysis(project.id);
      await loadProjects();
      return project;
    } catch (err: any) {
      console.error('Failed to create project or start analysis:', err);
      throw err;
    }
  };

  const handleStartAnalysis = async (projectId: string) => {
    try {
      await projectService.startAnalysis(projectId);
      await loadProjects();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to start analysis');
    }
  };

  const handleViewAnalysis = (projectId: string) => {
    // Navigate to code-graph page within the main app (embedded microfrontend)
    navigate(`/workspace/code-graph/${projectId}`);
  };

  const handleDeleteProject = async (projectId: string) => {
    if (!confirm('Are you sure you want to delete this project?')) return;

    try {
      await projectService.deleteProject(projectId);
      await loadProjects();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to delete project');
    }
  };

  return (
    <div className="space-y-8">
      {/* Stats Cards */}
      <div className="grid gap-6 md:grid-cols-3">
        <div className="glass rounded-xl p-6 hover:bg-gray-50 transition-all">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Projects</p>
              <p className="text-4xl font-bold text-gray-900 mt-2">{stats.total}</p>
            </div>
            <div className="h-14 w-14 rounded-xl gradient-primary flex items-center justify-center">
              <svg className="h-7 w-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="glass rounded-xl p-6 hover:bg-gray-50 transition-all">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Analyzing</p>
              <p className="text-4xl font-bold text-gray-700 mt-2">{stats.analyzing}</p>
            </div>
            {stats.analyzing > 0 ? (
              <div className="h-14 w-14 rounded-xl bg-gray-100 border border-gray-300 flex items-center justify-center">
                <svg className="h-7 w-7 text-gray-600 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            ) : (
              <div className="h-14 w-14 rounded-xl bg-green-50 border border-green-200 flex items-center justify-center">
                <svg className="h-7 w-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            )}
          </div>
        </div>

        <div className="glass rounded-xl p-6 hover:bg-gray-50 transition-all">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-4xl font-bold text-green-600 mt-2">{stats.completed}</p>
            </div>
            <div className="h-14 w-14 rounded-xl bg-green-50 border border-green-200 flex items-center justify-center">
              <svg className="h-7 w-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Modernization Workflow Sections */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Reverse Engineering */}
        <div className="glass rounded-xl p-6 hover:border-red-200 transition-all">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-lg gradient-primary flex items-center justify-center">
              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900">Reverse Engineering</h2>
          </div>

          <p className="text-gray-600 text-sm mb-6">Analyze and understand your existing codebase</p>

          <div className="space-y-3">
            <ToolCard
              icon={<CodeAnalysisIcon />}
              title="Code Analysis"
              description="Parse and analyze source code structure"
              status="available"
            />
            <ToolCard
              icon={<ASTIcon />}
              title="AST Parsing"
              description="Build abstract syntax trees with TreeSitter"
              status="available"
            />
            <ToolCard
              icon={<TechDebtIcon />}
              title="Tech Debt Analysis"
              description="Identify improvement areas and patterns"
              status="available"
            />
            <ToolCard
              icon={<RelationshipIcon />}
              title="Relationship Mapping"
              description="Map dependencies and relationships"
              status="available"
            />
          </div>
        </div>

        {/* Modernization Goals */}
        <div className="glass rounded-xl p-6 hover:border-red-200 transition-all">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-lg gradient-secondary flex items-center justify-center">
              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900">Modernization Goals</h2>
          </div>

          <p className="text-gray-600 text-sm mb-6">Define targets and patterns for transformation</p>

          <div className="space-y-3">
            <ToolCard
              icon={<TargetIcon />}
              title="Define Targets"
              description="Set modernization objectives and scope"
              status="available"
            />
            <ToolCard
              icon={<PatternIcon />}
              title="Select Patterns"
              description="Choose architectural and design patterns"
              status="available"
            />
            <ToolCard
              icon={<ConfigIcon />}
              title="Configure Options"
              description="Customize transformation parameters"
              status="available"
            />
            <ToolCard
              icon={<ValidationIcon />}
              title="Validation Rules"
              description="Set quality gates and constraints"
              status="available"
            />
          </div>
        </div>

        {/* Forward Engineering */}
        <div className="glass rounded-xl p-6 hover:border-red-200 transition-all">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-lg gradient-accent flex items-center justify-center">
              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900">Forward Engineering</h2>
          </div>

          <p className="text-gray-600 text-sm mb-6">Generate modern code, tests, and documentation</p>

          <div className="space-y-3">
            <ToolCard
              icon={<GenerateCodeIcon />}
              title="Generate Code"
              description="Create modernized codebase with latest patterns"
              status="available"
            />
            <ToolCard
              icon={<GenerateTestsIcon />}
              title="Generate Tests"
              description="Auto-generate unit and integration tests"
              status="available"
            />
            <ToolCard
              icon={<GenerateDocsIcon />}
              title="Generate Docs"
              description="Create comprehensive documentation"
              status="available"
            />
            <ToolCard
              icon={<ArchitectureIcon />}
              title="Architecture Diagrams"
              description="Visualize system architecture and flows"
              status="available"
            />
          </div>
        </div>
      </div>

      {/* Projects List */}
      <div className="glass rounded-xl overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Your Projects</h2>
          <p className="text-gray-600 mt-1">Manage your modernization projects</p>
        </div>
        <div className="p-6">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loading size="lg" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="mx-auto h-12 w-12 text-red-400">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading projects</h3>
              <p className="mt-1 text-sm text-gray-600">{error}</p>
              <div className="mt-6">
                <Button onClick={loadProjects}>Try Again</Button>
              </div>
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-12">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No projects yet</h3>
              <p className="mt-1 text-sm text-gray-600">
                Get started by creating your first modernization project.
              </p>
              <div className="mt-6">
                <Button onClick={() => setIsModalOpen(true)}>Create New Project</Button>
              </div>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {projects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  onViewAnalysis={handleViewAnalysis}
                  onStartAnalysis={handleStartAnalysis}
                  onDelete={handleDeleteProject}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* New Project Modal */}
      <NewProjectModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreateProject}
      />
    </div>
  );
}

// Tool Card Component
interface ToolCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  status: 'available' | 'coming-soon' | 'beta';
}

function ToolCard({ icon, title, description, status }: ToolCardProps) {
  return (
    <button className="w-full text-left p-4 rounded-lg bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-gray-300 transition-all group">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-200 transition-all">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            {status === 'beta' && (
              <span className="text-xs px-2 py-0.5 rounded bg-red-50 text-red-600 border border-red-200">Beta</span>
            )}
            {status === 'coming-soon' && (
              <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">Soon</span>
            )}
          </div>
          <p className="text-xs text-gray-600 group-hover:text-gray-700 transition-colors">{description}</p>
        </div>
      </div>
    </button>
  );
}

// Tool Icons
function CodeAnalysisIcon() {
  return (
    <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
    </svg>
  );
}

function ASTIcon() {
  return (
    <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
    </svg>
  );
}

function TechDebtIcon() {
  return (
    <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}

function RelationshipIcon() {
  return (
    <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );
}

function TargetIcon() {
  return (
    <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
    </svg>
  );
}

function PatternIcon() {
  return (
    <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z" />
    </svg>
  );
}

function ConfigIcon() {
  return (
    <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
    </svg>
  );
}

function ValidationIcon() {
  return (
    <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}

function GenerateCodeIcon() {
  return (
    <svg className="h-5 w-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function GenerateTestsIcon() {
  return (
    <svg className="h-5 w-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  );
}

function GenerateDocsIcon() {
  return (
    <svg className="h-5 w-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function ArchitectureIcon() {
  return (
    <svg className="h-5 w-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}
